// Work sidebar (design spec 2026-10-19 §4 "Work: Home / My tasks / Inbox rows; FAVORITES; SPACES
// tree"; Work OS panel.tsx + sidebar.tsx; Work UX pass 2026-10-20 "the sidebar is the
// navigation"): Boards (`/work/boards`, was Home `/work/home`: the overview) and My tasks (the inbox is the rail's, and
// Memory review is its Memory filter: UX pass 0.4, 1.4); FAVORITES, the spaces and boards the
// person starred; SPACES, the tree of spaces and their boards (drag a space into a group, drag a
// task onto a board), with New space and New group. Nothing listed here is listed again as a
// card on a page. Two calls, shared with the Work pages: `spaces.list` and `boards.list`. On an
// API that does not serve Work yet
// the panel keeps Boards and says so.
// M7-02: the tree also holds each space's pages (one more call, `pages.list` company-wide), with
// "+" on a space or page for a new page, and "Search pages" opens the pages search.
// DOCS-KB: a Docs row after Inbox (the Docs screen), starred docs under FAVORITES, and "+" opens
// the New doc template picker.
// MEMBER-UX: owners and admins get "Space settings" in each space's "…" menu.
// WORK-OS: boards lead under each space with their open counts (`boards.summary`, one more
// call), a space's pages fold into its Docs row, and the rows' hover controls open New board and
// New task here (the dialogs live in the sidebar, like New doc).
// PARITY-0: the footer's rows are a registry (`registerWorkFooterEntry`; Automations is one;
// Templates lives at Setup › Structure › Templates).
import { Button, NavList, NavRow, SidebarCaption, SidebarEmpty, SidebarPanel } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import {
  BookOpenIcon,
  ContactRoundIcon,
  FolderPlusIcon,
  LayoutDashboardIcon,
  ListTodoIcon,
  type LucideIcon,
  PlusIcon,
  SearchIcon,
  ZapIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { orderedRegistry } from "../../extensions/ordered-registry";
import type { SidebarProps } from "../../extensions/registry";
import { type CompanyAccess, isManager, isOperatorOnly } from "../../features/company";
import { ArchivedWork } from "../../features/work/archived-work";
import { addGroup, useTree } from "../../features/work/arrangement";
import { boardsOf } from "../../features/work/board-order";
import { DropOverlay, UploadDocDialog, useFileDrop } from "../../features/work/doc-upload";
import { useInboxUnread } from "../../features/work/inbox-list";
import { useSeatPeople } from "../../features/work/model";
import { NewDocDialog, PageSearchDialog } from "../../features/work/page-dialogs";
import { type NewPageTarget, SpacesTree } from "../../features/work/spaces-tree";
import type { Board } from "../../features/work/view-engine";
import {
  NewBoardDialog,
  NewGroupDialog,
  NewSpaceDialog,
  NewTaskDialog,
  SpaceSettingsDialog,
} from "../../features/work/work-dialogs";
import {
  boardSummariesQuery,
  boardsQuery,
  pageSharesQuery,
  pagesQuery,
  spacesQuery,
  workLiveQuery,
} from "../../features/work/work-queries";
import { keepFocus } from "../../focus/focus";
import { WorkFavorites, WorkFavoritesStrip, WorkRecents } from "./work-favorites";

/** What a footer row's `when` and `run` see. */
export type WorkFooterContext = { company: CompanyAccess };

/**
 * PARITY-0: a row of the Work sidebar's footer — a link (`href`, a `/c/$slug/…` route, opened
 * with the playhead) or an action (`run`). Automations is registered below; wave 1 adds Playbooks,
 * Automations and Forms (P5) and Trash (P4). No row shown: no footer.
 */
export type WorkFooterEntry = {
  id: string;
  title: string;
  /** Lower first; ties by id. */
  order: number;
  icon?: LucideIcon;
  /** Shown only when this says so (absent: always). */
  when?: (ctx: WorkFooterContext) => boolean;
} & ({ href: string } | { run: (ctx: WorkFooterContext) => void });

const footer = orderedRegistry<WorkFooterEntry>("work footer entry");

/** Adds a footer row; throws when its id is taken. Returns the unregister function. */
export function registerWorkFooterEntry(entry: WorkFooterEntry): () => void {
  return footer.register(entry);
}

/** Every registered footer row, sorted by order, then id. */
export function workFooterEntries(): WorkFooterEntry[] {
  return footer.list();
}

/** The footer rows this context shows (each row's `when`). */
export function visibleWorkFooterEntries(ctx: WorkFooterContext): WorkFooterEntry[] {
  return footer.list().filter((e) => !e.when || e.when(ctx));
}

// Parity P5: every work member gets Automations (its rules say who may change what).
registerWorkFooterEntry({
  id: "automations",
  title: "Automations",
  order: 80,
  icon: ZapIcon,
  href: "/c/$slug/work/automations",
});

// INC2-CONTACT: the contact core's plain list, for every work member.
registerWorkFooterEntry({
  id: "contacts",
  title: "Contacts",
  order: 70,
  icon: ContactRoundIcon,
  href: "/c/$slug/work/contacts",
});

export function WorkSidebar({ company, children }: SidebarProps) {
  const { api } = useRouteContext({ from: "__root__" });
  const live = useQuery(workLiveQuery(api, company.id));
  const on = live.data === true;
  const spaces = useQuery({ ...spacesQuery(api, company.id), enabled: on });
  const boards = useQuery({ ...boardsQuery(api, company.id), enabled: on });
  const pages = useQuery({ ...pagesQuery(api, company.id), enabled: on });
  // WORK-OS: the open count on each board row (one call, shared with Work home).
  const summaries = useQuery({ ...boardSummariesQuery(api, company.id), enabled: on });
  const people = useSeatPeople(api, company.id);
  // UX-A 0.4: no Inbox row here any more, but Work still keeps the rail's unread count fresh
  // (the rail reads the cache only; spec 13's call budget).
  useInboxUnread(api, company.id, on);
  const [newPage, setNewPage] = useState<NewPageTarget | null>(null);
  // MEMBER-UX: the space whose settings dialog is open (owners and admins).
  const [settingsOf, setSettingsOf] = useState<string | null>(null);
  const [newBoardIn, setNewBoardIn] = useState<string | null>(null);
  const [newTaskOn, setNewTaskOn] = useState<Board | null>(null);
  const [searching, setSearching] = useState(false);
  const [dropped, setDropped] = useState<File[] | null>(null);
  const drop = useFileDrop(setDropped);
  const [tree, setTree] = useTree(company.id);
  const params = { slug: company.slug };
  const operatorOnly = isOperatorOnly(company);
  const spaceList = spaces.data?.data ?? [];
  const boardList = boards.data?.data ?? [];
  const pageList = pages.data?.data ?? [];
  // MULTI-SPACE-ACCESS: docs shared into more spaces show there too, marked.
  const pageShares = useQuery({ ...pageSharesQuery(api, company.id), enabled: on });
  const settingsSpace = settingsOf ? spaceList.find((s) => s.id === settingsOf) : undefined;
  const summaryList = summaries.data;
  const byBoard = useMemo(
    () => new Map((summaryList ?? []).map((s) => [s.board_id, s] as const)),
    [summaryList],
  );

  const footerCtx: WorkFooterContext = { company };
  const footerRows = visibleWorkFooterEntries(footerCtx);

  const homeRow = (
    <NavRow
      icon={LayoutDashboardIcon}
      label="Boards"
      render={
        <Link
          to="/c/$slug/work/boards"
          params={params}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );
  const myTasksRow = (
    <NavRow
      icon={ListTodoIcon}
      label="My tasks"
      render={
        <Link
          to="/c/$slug/work/my"
          params={params}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );
  // T5-TASK-SEARCH: every task the person may open, by text, fields and dates.
  const searchRow = (
    <NavRow
      icon={SearchIcon}
      label="Search tasks"
      render={
        <Link
          to="/c/$slug/work/search"
          params={params}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );
  // DOCS-KB: every doc across the spaces (SOPs, policies, KB articles, pages).
  const docsRow = (
    <NavRow
      icon={BookOpenIcon}
      label="Docs"
      render={
        <Link
          to="/c/$slug/work/docs"
          params={params}
          search={keepFocus}
          activeOptions={{ includeSearch: false }}
        />
      }
    />
  );

  return (
    // KB-UPLOAD-2: files dropped on the sidebar open the Docs upload dialog with them.
    <div
      className="relative flex min-h-0 flex-1 flex-col"
      data-slot="work-sidebar-drop"
      data-dragging={drop.dragging ? "" : undefined}
      {...(on && !operatorOnly ? drop.handlers : {})}
    >
      {drop.dragging ? <DropOverlay label="Drop to add to Docs" /> : null}
      <UploadDocDialog
        api={api}
        company={company}
        open={dropped !== null}
        files={dropped ?? undefined}
        onClose={() => setDropped(null)}
      />
      <SidebarPanel
        title="Work"
        // SIDEBAR-TOGGLE-FIXED: collapsed, Home, My tasks, Docs and the favorites as icons.
        strip={
          <>
            <NavList>
              {homeRow}
              {myTasksRow}
              {docsRow}
            </NavList>
            {on && !operatorOnly ? <WorkFavoritesStrip api={api} company={company} /> : null}
          </>
        }
        footer={
          footerRows.length ? (
            <NavList>
              {footerRows.map((r) => (
                <NavRow
                  key={r.id}
                  {...(r.icon ? { icon: r.icon } : {})}
                  label={r.title}
                  render={
                    "href" in r ? (
                      <Link
                        to={r.href as never}
                        params={params as never}
                        search={keepFocus as never}
                        activeOptions={{ includeSearch: false }}
                      />
                    ) : (
                      <button type="button" onClick={() => r.run(footerCtx)} />
                    )
                  }
                />
              ))}
            </NavList>
          ) : null
        }
      >
        <NavList>
          {homeRow}
          {myTasksRow}
          {searchRow}
          {docsRow}
        </NavList>

        {live.data === false ? (
          <p className="px-4 py-2 text-xs text-muted-foreground">
            Spaces, boards and tasks are not live on this server yet: they arrive with the boards
            build (M2-03).
          </p>
        ) : null}

        {/* WOS-BUNDLE: Favorites (the server's, in the person's order) and Recent, on top. */}
        {/* OPERATOR-ONLY-UX: both are a person member's; an operator-only caller has neither. */}
        {on && !operatorOnly ? <WorkFavorites api={api} company={company} /> : null}
        {on && !operatorOnly ? <WorkRecents api={api} company={company} /> : null}

        {on ? (
          <>
            <SidebarCaption
              label="Spaces"
              action={
                <span className="flex items-center">
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Search pages"
                    onClick={() => setSearching(true)}
                  >
                    <SearchIcon aria-hidden="true" />
                  </Button>
                  <NewGroupDialog
                    onCreate={(name, id) =>
                      setTree(
                        addGroup(
                          tree,
                          spaceList.map((s) => s.id),
                          name,
                          id,
                        ),
                      )
                    }
                    trigger={
                      <Button variant="ghost" size="icon-xs" aria-label="New group">
                        <FolderPlusIcon aria-hidden="true" />
                      </Button>
                    }
                  />
                  <NewSpaceDialog
                    api={api}
                    company={company}
                    trigger={
                      <Button variant="ghost" size="icon-xs" aria-label="New space">
                        <PlusIcon aria-hidden="true" />
                      </Button>
                    }
                  />
                </span>
              }
            />
            {spaces.isSuccess && !spaceList.length ? (
              // OPERATOR-ONLY-UX: the page shows the notice; + is disabled with the reason.
              <SidebarEmpty
                things="spaces"
                {...(operatorOnly ? {} : { action: "Add one with +" })}
              />
            ) : (
              <SpacesTree
                api={api}
                company={company}
                spaces={spaceList}
                boards={boardList}
                summaries={byBoard}
                pages={pageList}
                sharedPages={pageShares.data?.data ?? []}
                onNewPage={setNewPage}
                onSpaceSettings={isManager(company) ? (s) => setSettingsOf(s.id) : undefined}
                onNewBoard={setNewBoardIn}
                onNewTask={setNewTaskOn}
                tree={tree}
                onTreeChange={setTree}
              />
            )}
            <ArchivedWork api={api} company={company} enabled={on} />
            {settingsSpace ? (
              <SpaceSettingsDialog
                key={settingsSpace.id}
                api={api}
                company={company}
                space={settingsSpace}
                boards={boardsOf(boardList, settingsSpace.id)}
                onClose={() => setSettingsOf(null)}
              />
            ) : null}
            <NewDocDialog
              api={api}
              company={company}
              target={newPage}
              onClose={() => setNewPage(null)}
            />
            {newBoardIn ? (
              <NewBoardDialog
                key={newBoardIn}
                api={api}
                company={company}
                spaceId={newBoardIn}
                open={true}
                onOpenChange={(o) => (o ? null : setNewBoardIn(null))}
              />
            ) : null}
            {newTaskOn ? (
              <NewTaskDialog
                key={newTaskOn.id}
                api={api}
                company={company}
                board={newTaskOn}
                people={people}
                open={true}
                onOpenChange={(o) => (o ? null : setNewTaskOn(null))}
              />
            ) : null}
            <PageSearchDialog
              api={api}
              company={company}
              open={searching}
              onOpenChange={setSearching}
            />
            {spaces.isError || boards.isError ? (
              <p className="px-4 py-1 text-xs text-muted-foreground">
                The spaces could not be loaded.
              </p>
            ) : null}
          </>
        ) : null}
        {children}
      </SidebarPanel>
    </div>
  );
}
